import React from "react";
import { FaClipboard } from "react-icons/fa";
import { Alert } from "react-bootstrap";
import { Reference, FetchResult } from "@apollo/client";
import {
  ADD_ASSUMPTION,
  EDIT_ASSUMPTION,
  DELETE_ASSUMPTION,
  REORDER_ASSUMPTIONS,
} from "../../graphql-client/queries/project";
import type {
  Scalars,
  Assumption,
  AddAssumptionMutation,
  Project,
} from "../../graphql-client/codegen-types";
import { useProjectMutation } from "./project-context";
import DynamicList from "./DynamicList";
import Explanation from "../../components/Explanation";
import EntradaTexto from "../../components/EntradaTexto";
import client from "../../graphql-client/connection";

type ID = Scalars["ID"];

type Props = {
  project: Project;
  readOnly: boolean;
};

function addAssumptionCallback({
  data,
}: FetchResult<AddAssumptionMutation>): Assumption {
  const ret = data?.asUser?.editProject?.addAssumption?.newAssumption;
  if (!ret) throw new Error("Error creating assumption");
  return ret;
}

const Assumptions = ({ project, readOnly }: Props) => {
  const { cache } = client;
  const addAssumption = useProjectMutation<
    AddAssumptionMutation,
    { projectId: ID }
  >(ADD_ASSUMPTION);
  const editAssumption = useProjectMutation(EDIT_ASSUMPTION);
  const deleteAssumption = useProjectMutation(DELETE_ASSUMPTION);
  const reorderAssumptions = useProjectMutation(REORDER_ASSUMPTIONS);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [errorDetail, setErrorDetail] = React.useState("");
  return (
    <div>
      <DynamicList
        title={
          <span>
            {" "}
            <FaClipboard /> Suposiciones{" "}
          </span>
        }
        deleteInfo={
          readOnly
            ? undefined
            : {
                onDelete: ({ id }: Assumption) => {
                  cache.modify({
                    id: cache.identify(project),
                    fields: {
                      assumptions: (oldAssumptions, { readField }) =>
                        oldAssumptions.filter(
                          (assumptionRef: Reference) =>
                            id !== readField("id", assumptionRef)
                        ),
                    },
                  });
                  return deleteAssumption({
                    variables: { projectId: project.id, id },
                  }).then(() => true);
                },
                deleteWarnTitle: "¿Eliminar beneficio?",
                deleteWarnMsg: "No se podrá recuperar.",
              }
        }
        renderItem={(assumption: Assumption, isNew: boolean) => (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              background: "whitesmoke",
              borderRadius: "5px",
              padding: "5px",
            }}
          >
            <FaClipboard style={{ margin: "15px 5px 5px 5px" }} />
            <div style={{ width: "100%" }}>
              <EntradaTexto
                ejemplo="Que el cliente va a asignar un representante para darnos información."
                maxLetras={280}
                autoFocus={isNew}
                saveText={
                  readOnly
                    ? () => {}
                    : (text: string) =>
                        editAssumption({
                          variables: {
                            projectId: project.id,
                            input: { id: assumption.id, text },
                          },
                          update() {
                            cache.modify({
                              id: cache.identify(assumption),
                              fields: {
                                text: () => text,
                              },
                            });
                          },
                        })
                }
                textoInicial={assumption.text ?? ""}
                rows={2}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}
        getItemId={({ id }: Assumption) => id}
        initial={project.assumptions || []}
        newItem={
          readOnly
            ? undefined
            : () =>
                addAssumption({
                  variables: { projectId: project.id },
                })
                  .then(addAssumptionCallback)
                  .then((newAssumption: Assumption) => {
                    cache.modify({
                      id: cache.identify(project),
                      fields: {
                        assumptions: (oldAssumptions) =>
                          oldAssumptions.concat({
                            __ref: cache.identify(newAssumption),
                          }),
                      },
                    });
                    return newAssumption;
                  })
                  .catch((e) => {
                    const applicationErrorDetail =
                      e?.graphQLErrors?.[0]?.extensions?.applicationErrorDetail;
                    const projectError = applicationErrorDetail?.projectError;
                    if (projectError?.assumptionsLimitExceeded) {
                      setErrorMessage(
                        `No más de ${projectError.assumptionsLimitExceeded} beneficios por proyecto`
                      );
                      setErrorDetail(
                        "Demasiados beneficios son difíciles de monitorear"
                      );
                      setTimeout(() => {
                        setErrorMessage("");
                        setErrorDetail("");
                      }, 7000);
                      return null;
                    }
                    throw e;
                  })
        }
        onReorder={
          readOnly
            ? undefined
            : (assumptions: Array<Assumption>) =>
                reorderAssumptions({
                  variables: {
                    projectId: project.id,
                    idList: assumptions.map(({ id }: Assumption) => id),
                  },
                  update() {
                    cache.modify({
                      id: cache.identify(project),
                      fields: {
                        assumptions: () =>
                          assumptions.map((assumption) => ({
                            __ref: cache.identify(assumption),
                          })),
                      },
                    });
                  },
                })
        }
      />
      {errorMessage !== "" && (
        <Alert variant="warning">
          <Explanation text={errorMessage} detail={errorDetail} />
        </Alert>
      )}
    </div>
  );
};

export default Assumptions;
